import { useEffect, useMemo, useState, useCallback } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { get as getPersistValue } from "utils/persist_store"
import _, { isNil } from "lodash"

import { useRoute } from "providers/route"
import { useVenue } from "providers/venue"
import { gtmPushDataLayerGetDirection } from "utils/googletagmanager"
import {
  findNearestDuplexLocationByOrigin,
  normalizeRouteLocation,
  transformGTMDirectionData,
} from "utils/direction"
import config from "config"
import { ERROR_STRATEGY } from "constant"
import { sendCaptureExceptionToSentry } from "utils/sentry"
import { useAppUI } from "providers/ui"

const debounceGtmPushDataLayerGetDirection = _.debounce(
  gtmPushDataLayerGetDirection,
  800
)

export const useDirection = () => {
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false)
  const [isError, setIsError] = useState(false)
  const { findRoute, clearRoute, route, findElevatorPreferredRoute } =
    useRoute()
  const {
    transformLocationToFeature,
    relationshipGraphLoaded,
    defaultRoutePath: exitPath,
  } = useVenue()
  const {
    origin: originParams,
    destination: destinationParams,
    step: stepParams,
  } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const origin = useMemo(
    () => transformLocationToFeature(originParams),
    [originParams, transformLocationToFeature]
  )
  const locationName = getPersistValue("venue_device_name")
  const { isWheelchairModeEnabled } = useAppUI()

  const destination = useMemo(
    () => transformLocationToFeature(destinationParams),
    [destinationParams, transformLocationToFeature]
  )

  const stepIndex = Number(stepParams)
  const isNavigating = Number.isInteger(stepIndex) && stepIndex >= 1

  const elevatorPreferredMode = useMemo(() => {
    // If isWheelchairMode is true, elevatorPreferredMode is always true
    if (isWheelchairModeEnabled) return true
    const isAvoidSteps = searchParams.get("avoidSteps")
    return isAvoidSteps === "true"
  }, [searchParams, isWheelchairModeEnabled])

  const toggleElevatorPreference = useCallback(() => {
    // Disable toggle if isWheelchairMode is true
    if (isWheelchairModeEnabled) return
    const currentParams = Object.fromEntries(searchParams.entries())
    setSearchParams(
      { ...currentParams, avoidSteps: !elevatorPreferredMode },
      { replace: true }
    )
  }, [
    elevatorPreferredMode,
    searchParams,
    setSearchParams,
    isWheelchairModeEnabled,
  ])

  useEffect(() => {
    if (relationshipGraphLoaded && origin && destination) {
      //NOTE: this is temporary solution
      //TODO: find better solution for direction overview loading screen
      setIsCalculatingRoute(true)
      setTimeout(async () => {
        try {
          const nearestOriginFeature = findNearestDuplexLocationByOrigin(
            destination,
            origin
          )
          const nearestDestFeature = findNearestDuplexLocationByOrigin(
            origin,
            destination
          )

          if (nearestOriginFeature?.id === nearestDestFeature?.id) {
            setIsCalculatingRoute(false)
            return
          }

          if (!elevatorPreferredMode) {
            await findRoute(nearestOriginFeature, nearestDestFeature)
          }
          if (elevatorPreferredMode) {
            await findElevatorPreferredRoute(
              nearestOriginFeature,
              nearestDestFeature
            )
          }
          debounceGtmPushDataLayerGetDirection(
            transformGTMDirectionData(origin),
            transformGTMDirectionData(destination),
            locationName,
            elevatorPreferredMode
          )
          setIsCalculatingRoute(false)
        } catch (error) {
          sendCaptureExceptionToSentry({
            error,
            level: "error",
            transactionName: `Failed to navigate from ${origin?.id} to ${destination?.id}.`,
          })
          switch (config("navigationErrorStrategy")) {
            case ERROR_STRATEGY.RELOAD:
              window.location.replace("/")
              break
            case ERROR_STRATEGY.DIALOG:
            default:
              setIsError(true)
              return
          }
        }
      }, 500)
    }
    return () => clearRoute()
  }, [
    origin,
    destination,
    relationshipGraphLoaded,
    clearRoute,
    findRoute,
    elevatorPreferredMode,
    findElevatorPreferredRoute,
    locationName,
  ])

  const normalizedRoute = useMemo(() => {
    if (isNil(route)) return null
    // Preserve name, id, and id of origin/destination from the original route to:
    // - Ensure correct UI display
    // - Allow switching between origin and destination using the same data
    return {
      ...route,
      origin: normalizeRouteLocation(origin, route.origin),
      destination: normalizeRouteLocation(destination, route.destination),
    }
  }, [route, origin, destination])

  return {
    route: normalizedRoute,
    isNavigating,
    isError,
    isCalculatingRoute,
    origin,
    destination,
    exitPath,
    // Function
    elevatorPreferredMode,
    toggleElevatorPreference,
  }
}
